// The clinician's triage panel (TGCLOUD-1910), in the pedigree sidebar's Risk
// factors section beside the risk models. It evaluates the person's family
// against each rule pack the group uses, at the version the group chose. Each
// pack is a small tile saying whether its criteria are met; a click opens the
// full result in its published wording (Tim, QA 2026-10-16: drawn in full, the
// results took the whole section). "Record result" stores the evaluation beside
// the risk records; a stored result keeps its pack version, so a newer pack
// never rewrites it.
//
// It reads the SAVED pedigree, as CanRisk does: the canvas saves as it goes,
// and "Check again" reads it afresh.
import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import RadioButtonUncheckedOutlinedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { SnackContext } from 'src/store/ContextStore';
import { SgAccessContext } from 'src/components/manage-users/SgAcessProvider';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { CLINICAL_COLOURS } from 'src/theme/palette';
import PackResult from './PackResult';
import { outcomeShortText } from './triageText';

// What an answer the panel cannot draw means.
const quietCodes = new Set(['TRIAGE_OFF', 'TRIAGE_FORBIDDEN']);

const packNameOf = (pack) => pack.shortName || pack.name || pack.packId;
const isMet = (pack) =>
  pack.status === 'evaluated' &&
  Boolean(pack.outcome) &&
  pack.outcome !== 'DOES_NOT_MEET';

/**
 * One pack's tile: its name and whether its criteria are met, drawn as a risk
 * record's tile is (RiskRecordsList), the edge in the Risk Score colour when
 * they are. A click opens the full result.
 */
function PackTile({ pack, onOpen }) {
  const t = useAutoTranslation();
  const met = isMet(pack);
  const evaluated = pack.status === 'evaluated' && Boolean(pack.outcome);
  const Icon = met
    ? CheckCircleOutlinedIcon
    : evaluated
    ? RadioButtonUncheckedOutlinedIcon
    : RemoveCircleOutlineOutlinedIcon;
  const outcome = outcomeShortText(pack, t);
  return (
    <Box
      component="button"
      type="button"
      onClick={onOpen}
      data-testid="triage-tile"
      data-pack={pack.packId}
      aria-label={t('{{pack}}: {{outcome}}. Show the full result', {
        pack: packNameOf(pack),
        outcome
      })}
      sx={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        font: 'inherit',
        cursor: 'pointer',
        border: '1px solid #e0e0e0',
        borderLeft: `3px solid ${met ? CLINICAL_COLOURS.riskScore : '#bdbdbd'}`,
        borderRadius: '6px',
        p: '8px 10px',
        bgcolor: '#fff',
        '&:hover': { bgcolor: '#fafafa' },
        '&:focus-visible': { outline: 2, outlineOffset: 2 }
      }}
    >
      <Typography
        component="span"
        sx={{
          display: 'block',
          fontSize: '10px',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'text.secondary',
          lineHeight: 1.3
        }}
      >
        {packNameOf(pack)}
      </Typography>
      <Box
        component="span"
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}
      >
        <Icon
          aria-hidden="true"
          sx={{
            fontSize: 16,
            color: met ? CLINICAL_COLOURS.riskScore : 'text.secondary'
          }}
        />
        <Typography
          component="span"
          variant="body2"
          sx={{ fontWeight: met ? 700 : 400, lineHeight: 1.3 }}
        >
          {outcome}
        </Typography>
      </Box>
      {met && pack.conditional && (
        <Typography
          component="span"
          variant="caption"
          sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}
        >
          {t('On what the record holds: to be confirmed')}
        </Typography>
      )}
    </Box>
  );
}

/**
 * @param {object} props
 * @param {string} props.userId          the family root
 * @param {string} props.familyMemberId  the person (proBandId for the proband)
 * @param {number} [props.refreshKey]    re-evaluate when it moves (a new CanRisk
 *   result can change a risk-estimate criterion)
 * @param {Function} [props.onRecorded]  after a result is stored
 * @param {Function} [props.onEvaluation] (evaluation|null) => void, for the PDF
 */
export default function TriagePanel({
  userId,
  familyMemberId,
  refreshKey = 0,
  onRecorded,
  onEvaluation
}) {
  const t = useAutoTranslation();
  const { setSnack } = React.useContext(SnackContext);
  const { readOnly } = React.useContext(SgAccessContext);
  const [state, setState] = React.useState({ status: 'idle', data: null });
  const [again, setAgain] = React.useState(0);
  const [recording, setRecording] = React.useState(false);
  // The pack whose full result is open, by id: a fresh evaluation replaces it.
  const [openPackId, setOpenPackId] = React.useState(null);

  const url = userId
    ? APICONSTANTS.triageEvaluate.replace(':user_id', userId)
    : null;

  React.useEffect(() => {
    if (!url) return undefined;
    let live = true;
    setState({ status: 'loading', data: null });
    axios
      .get(url, {
        params: familyMemberId ? { familyMemberId } : undefined
      })
      .then(({ data }) => {
        if (live) setState({ status: 'ready', data });
      })
      .catch((err) => {
        if (!live) return;
        const code = err?.response?.data?.code;
        setState({
          status: quietCodes.has(code) ? 'off' : 'error',
          code: code || null,
          data: null
        });
      });
    return () => {
      live = false;
    };
  }, [url, familyMemberId, refreshKey, again]);

  React.useEffect(() => {
    if (onEvaluation)
      onEvaluation(state.status === 'ready' ? state.data : null);
  }, [onEvaluation, state]);

  const record = () => {
    if (!url) return;
    setRecording(true);
    axios
      .post(url, { familyMemberId })
      .then(({ data }) => {
        setState({ status: 'ready', data });
        const count = data?.recorded?.length || 0;
        setSnack({
          status: true,
          msg:
            count === 1
              ? t('Triage result recorded')
              : t('{{count}} triage results recorded', { count }),
          severity: 'success'
        });
        if (onRecorded) onRecorded();
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg:
            err?.response?.status === 403 &&
            err?.response?.data?.message === 'Read-only access'
              ? t(
                  'Your security group is read-only: the result was not recorded.'
                )
              : t('The triage result could not be recorded.'),
          severity: 'error'
        });
      })
      .finally(() => setRecording(false));
  };

  if (state.status === 'off' || state.status === 'idle') return null;

  const packs = state.data?.packs || [];
  const evaluated = packs.filter((p) => p.status === 'evaluated');
  const canRecord = !readOnly('pedigrees') && evaluated.length > 0;

  const openPack = packs.find((p) => `${p.packId}@${p.version}` === openPackId);

  return (
    <Box data-testid="triage-panel" sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <Typography
          sx={{
            fontSize: '.75rem',
            fontWeight: 700,
            letterSpacing: '.06em',
            textTransform: 'uppercase',
            color: 'text.secondary',
            flex: 1
          }}
        >
          {t('Triage')}
        </Typography>
        {state.status === 'ready' && canRecord && (
          <Button
            size="small"
            onClick={record}
            disabled={recording}
            data-testid="triage-record"
          >
            {t('Record result')}
          </Button>
        )}
        {state.status === 'ready' && packs.length > 0 && (
          <Tooltip title={t('Check again')}>
            <span>
              <IconButton
                size="small"
                aria-label={t('Check again')}
                onClick={() => setAgain((n) => n + 1)}
                disabled={recording}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      {state.status === 'loading' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2">
            {t('Checking the family against the rule packs…')}
          </Typography>
        </Box>
      )}

      {state.status === 'error' && (
        <Box sx={{ py: 1 }}>
          <Typography variant="body2">
            {state.code === 'PERSON_NOT_FOUND'
              ? t(
                  'This person is not in the family record yet. Save the pedigree, then check again.'
                )
              : t('Triage could not be evaluated.')}
          </Typography>
          <Button size="small" onClick={() => setAgain((n) => n + 1)}>
            {t('Check again')}
          </Button>
        </Box>
      )}

      {state.status === 'ready' && packs.length === 0 && (
        <Typography variant="body2" sx={{ py: 1 }}>
          {t('No triage rule packs are switched on for this group.')}
        </Typography>
      )}

      {state.status === 'ready' && packs.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 1
          }}
        >
          {packs.map((pack) => (
            <PackTile
              key={`${pack.packId}@${pack.version}`}
              pack={pack}
              onOpen={() => setOpenPackId(`${pack.packId}@${pack.version}`)}
            />
          ))}
        </Box>
      )}

      <Dialog
        open={Boolean(openPack)}
        onClose={() => setOpenPackId(null)}
        maxWidth="sm"
        fullWidth
      >
        {openPack && (
          <>
            <DialogTitle sx={{ pb: 1, pr: 6 }}>
              {t('Triage: {{pack}}', { pack: packNameOf(openPack) })}
              <IconButton
                aria-label={t('close')}
                onClick={() => setOpenPackId(null)}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  color: 'inherit'
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <PackResult result={openPack} />
            </DialogContent>
            <DialogActions>
              <Button variant="outlined" onClick={() => setOpenPackId(null)}>
                {t('Close')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
